// src/pages/intranet/pages/DatosLogia.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Building, Award, User, Users, Link as LinkIcon, Edit, Mail, CheckCircle, Plus, X, Save, Shield } from 'lucide-react';

export default function DatosLogia() {
  const { user } = useAuth();
  
  // Extracción segura para evitar errores 'undefined'
  const currentUserId = user?.id || user?.user?.id;
  const currentLogiaId = user?.logia_id || user?.user?.logia_id;
  const currentUsername = user?.username || user?.user?.username;
  const currentUserRole = user?.role || user?.user?.role;
  
  const [activeTab, setActiveTab] = useState('perfil'); 
  const [linkCopiado, setLinkCopiado] = useState(false);
  
  // Estados para Data Dinámica
  const [perfilData, setPerfilData] = useState({});
  const [familiares, setFamiliares] = useState([]);
  const [reconocimientos, setReconocimientos] = useState([]);
  const [logiaData, setLogiaData] = useState({});
  const [miembrosLogia, setMiembrosLogia] = useState([]);

  // Estados de Modales y Edición
  const [isModalMiembroOpen, setIsModalMiembroOpen] = useState(false);
  const [isModalFamiliarOpen, setIsModalFamiliarOpen] = useState(false);
  const [miembroSeleccionado, setMiembroSeleccionado] = useState(null);
  const [editandoLogia, setEditandoLogia] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (currentUserId) {
      cargarPerfil();
      cargarFamiliares();
      cargarReconocimientos();
    }
    if (currentLogiaId) {
      cargarDatosLogia();
    }
  }, [currentUserId, currentLogiaId]);

  useEffect(() => {
    if (activeTab === 'directorio' && currentLogiaId) {
      cargarMiembros();
    }
  }, [activeTab, currentLogiaId]);

  // --- FETCHERS ---
  const cargarPerfil = async () => {
    if (!currentUserId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/perfil/${currentUserId}`);
      const data = await res.json();
      if (data.success) setPerfilData(data.data);
    } catch (error) { console.error("Error cargando perfil", error); }
  };

  const cargarFamiliares = async () => {
    if (!currentUserId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/${currentUserId}/familiares`);
      const data = await res.json();
      if (data.success) setFamiliares(data.data);
    } catch (error) { console.error("Error cargando familiares", error); }
  };

  const cargarReconocimientos = async () => {
    if (!currentUserId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/${currentUserId}/reconocimientos`);
      const data = await res.json();
      if (data.success) setReconocimientos(data.data);
    } catch (error) { console.error("Error cargando reconocimientos", error); }
  };

  const cargarDatosLogia = async () => {
    if (!currentLogiaId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/logias/info/${currentLogiaId}`);
      const data = await res.json();
      if (data.success) setLogiaData(data.data);
    } catch (error) { console.error("Error cargando logia", error); }
  };

  const cargarMiembros = async () => {
    if (!currentLogiaId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/logia/${currentLogiaId}`);
      const data = await res.json();
      if (data.success) setMiembrosLogia(data.data);
    } catch (error) { console.error("Error cargando directorio", error); }
  };

  // --- HANDLERS ---
  const handleGuardarPerfil = async () => {
    if (!currentUserId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/perfil/${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(perfilData)
      });
      const data = await res.json();
      if (data.success) alert('Perfil actualizado correctamente.');
    } catch (error) { alert('Error al guardar.'); }
  };

  const handleAgregarFamiliar = async (nuevoFamiliar) => {
    if (!currentUserId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/${currentUserId}/familiares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoFamiliar)
      });
      const data = await res.json();
      if (data.success) {
        setIsModalFamiliarOpen(false);
        cargarFamiliares();
      }
    } catch (error) { alert('Error al añadir familiar.'); }
  };

  const handleGuardarLogia = async () => {
    if (!currentLogiaId) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/logias/info/${currentLogiaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logiaData)
      });
      const data = await res.json();
      if (data.success) {
        alert('Datos de la Logia actualizados.');
        setEditandoLogia(false);
      }
    } catch (error) { alert('Error al guardar logia.'); }
  };

  const handleCopiarEnlace = async () => {
    if (!currentUserId) return;
    try {
      const response = await fetch('https://logia-production-725b.up.railway.app/api/invitaciones/generar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ horas_validez: 48, usuario_id: currentUserId })
      });
      const data = await response.json();
      if (data.success) {
        const token = data.url.split('ref=')[1] || '';
        navigator.clipboard.writeText(`https://glp.nexogar.com/registro-publico/ref=${token}`);
        setLinkCopiado(true);
        setTimeout(() => setLinkCopiado(false), 3000);
      }
    } catch (error) { console.error("Error:", error); }
  };

  const handleAltaManual = async (formData) => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/usuarios/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, logia_id: currentLogiaId })
      });
      const data = await res.json();
      if (data.success) {
        setIsModalMiembroOpen(false);
        cargarMiembros();
        alert('Hermano registrado exitosamente.');
      } else {
        alert(data.error || 'Error al registrar hermano.');
      }
    } catch (error) {
      alert('Error de conexión al registrar.');
    }
  };

  return (
    <div className="flex flex-col gap-6 lg:gap-8 animate-fade-in relative">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-white m-0 tracking-wide">Archivo y Directorio</h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Gestión de perfiles y datos institucionales</p>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 shadow-2xl overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        
        <div className="flex border-b border-white/10 overflow-x-auto hide-scrollbar relative z-10 bg-[#050505]/50">
          <button 
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold whitespace-nowrap border-b-2 ${activeTab === 'perfil' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} 
            onClick={() => setActiveTab('perfil')}
          >
            <User size={16} /> Mi Perfil
          </button>
          
          <button 
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold whitespace-nowrap border-b-2 ${activeTab === 'institucional' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} 
            onClick={() => setActiveTab('institucional')}
          >
            <Building size={16} /> Institucional
          </button>

          {(currentUserRole === 'COMPAÑERO' || currentUserRole === 'MAESTRO') && (
            <button 
              className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold whitespace-nowrap border-b-2 ${activeTab === 'directorio' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'}`} 
              onClick={() => setActiveTab('directorio')}
            >
              <Users size={16} /> Directorio de HH:.
            </button>
          )}
        </div>

        <div className="p-6 md:p-10 relative z-10">
          
          {/* PESTAÑA 1: MI PERFIL */}
          {activeTab === 'perfil' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start animate-fade-in">
              <div className="lg:col-span-4 flex flex-col items-center gap-4 p-8 bg-[#050505] border border-white/5 shadow-lg">
                <div className="w-24 h-24 rounded-sm bg-gradient-to-br from-[#111111] to-[#050505] border border-[#C5A059]/40 text-[#C5A059] flex justify-center items-center font-serif text-4xl shadow-[0_0_15px_rgba(197,160,89,0.15)]">
                  {currentUsername?.charAt(0) || 'H'}
                </div>
                <h2 className="m-0 text-xl font-serif text-white text-center">{currentUsername || 'Cargando...'}</h2>
                <span className="bg-white/10 border border-white/20 text-gray-300 px-3 py-1 text-[10px] uppercase tracking-widest font-mono">
                  {currentUserRole || 'APRENDIZ'}
                </span>
                <button className="mt-4 flex items-center gap-2 bg-transparent border border-white/20 text-gray-400 hover:text-white hover:border-[#C5A059] px-4 py-2 text-xs tracking-widest uppercase transition-colors">
                  <Edit size={14}/> Cambiar Foto
                </button>
              </div>

              <div className="lg:col-span-8 flex flex-col">
                <h3 className="font-serif text-xl md:text-2xl text-white m-0 mb-6 border-b border-white/10 pb-3">Información Personal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-gray-500 text-[10px] tracking-[0.2em] uppercase font-semibold">Nombre Completo</label>
                    <input type="text" disabled value={`${perfilData.nombres || ''} ${perfilData.apellidos || ''}`.trim()} className="bg-[#111111] border border-white/5 text-gray-500 px-4 py-3 text-sm cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-gray-500 text-[10px] tracking-[0.2em] uppercase font-semibold">Correo Electrónico</label>
                    <input type="email" disabled value={perfilData.email || ''} className="bg-[#111111] border border-white/5 text-gray-500 px-4 py-3 text-sm cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Teléfono</label>
                    <input type="tel" value={perfilData.telefono || ''} onChange={(e) => setPerfilData({...perfilData, telefono: e.target.value})} className="bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] transition-colors" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Profesión / Oficio</label>
                    <select 
                      value={perfilData.profesion || ''} 
                      onChange={(e) => setPerfilData({...perfilData, profesion: e.target.value})} 
                      className="bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">Seleccionar Profesión</option>
                      <option value="Abogado">Abogado</option>
                      <option value="Arquitecto">Arquitecto</option>
                      <option value="Comerciante">Comerciante</option>
                      <option value="Contador">Contador</option>
                      <option value="Docente">Docente</option>
                      <option value="Empresario">Empresario</option>
                      <option value="Estudiante">Estudiante</option>
                      <option value="Ingeniero de Sistemas / Software">Ingeniero de Sistemas / Software</option>
                      <option value="Ingeniero (Otras ramas)">Ingeniero (Otras ramas)</option>
                      <option value="Médico / Salud">Médico / Salud</option>
                      <option value="Militar / Fuerzas Armadas">Militar / Fuerzas Armadas</option>
                      <option value="Policía">Policía</option>
                      <option value="Sector Público">Sector Público</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Especialización (Opcional)</label>
                    <input type="text" placeholder="Ej. Frontend, Derecho Penal" value={perfilData.especializacion || ''} onChange={(e) => setPerfilData({...perfilData, especializacion: e.target.value})} className="bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] transition-colors" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Tipo de Sangre</label>
                    <select value={perfilData.tipo_sangre || ''} onChange={(e) => setPerfilData({...perfilData, tipo_sangre: e.target.value})} className="bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] transition-colors appearance-none cursor-pointer">
                      <option value="">Seleccionar</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                    </select>
                  </div>
                </div>

                <div className="mt-10 flex justify-end mb-8">
                  <button onClick={handleGuardarPerfil} className="bg-[#C5A059] hover:bg-white text-[#050505] px-8 py-3 text-xs tracking-widest uppercase font-bold transition-colors">
                    Guardar Perfil
                  </button>
                </div>

                {/* HISTÓRICO DE RECONOCIMIENTOS (Solo lectura en Mi Perfil) */}
                <div className="flex justify-between items-end mb-6 border-b border-white/10 pb-3 mt-8">
                  <h3 className="font-serif text-xl md:text-2xl text-white m-0">Histórico de Reconocimientos</h3>
                  <span className="text-gray-500 text-[10px] tracking-widest uppercase">Asignado por Secretaría</span>
                </div>
                
                <div className="overflow-x-auto bg-[#050505] border border-white/5 mb-10">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Título / Reconocimiento</th>
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Descripción</th>
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconocimientos.map((rec) => (
                        <tr key={rec.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-4 px-6 text-sm text-gray-300 font-medium">{rec.titulo}</td>
                          <td className="py-4 px-6 text-sm text-gray-400">{rec.descripcion || '-'}</td>
                          <td className="py-4 px-6 text-sm text-gray-400">{rec.fecha_otorgamiento ? new Date(rec.fecha_otorgamiento).toLocaleDateString('es-PE') : '-'}</td>
                        </tr>
                      ))}
                      {reconocimientos.length === 0 && (
                        <tr><td colSpan="3" className="py-6 text-center text-gray-600 text-xs uppercase tracking-widest">No hay histórico registrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* CÍRCULO FAMILIAR */}
                <div className="flex justify-between items-end mb-6 border-b border-white/10 pb-3">
                  <h3 className="font-serif text-xl md:text-2xl text-white m-0">Círculo Familiar</h3>
                  <button onClick={() => setIsModalFamiliarOpen(true)} className="text-[#C5A059] text-[10px] tracking-widest uppercase hover:text-white transition-colors flex items-center gap-1">
                    <Plus size={14} /> Añadir Familiar
                  </button>
                </div>
                
                <div className="overflow-x-auto bg-[#050505] border border-white/5">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Nombres</th>
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Parentesco</th>
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Edad</th>
                        <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Profesión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {familiares.map((fam) => (
                        <tr key={fam.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-4 px-6 text-sm text-gray-300 font-medium">{fam.nombres}</td>
                          <td className="py-4 px-6 text-sm text-gray-400">{fam.parentesco}</td>
                          <td className="py-4 px-6 text-sm text-gray-400">{fam.edad}</td>
                          <td className="py-4 px-6 text-sm text-gray-400">{fam.profesion || '-'}</td>
                        </tr>
                      ))}
                      {familiares.length === 0 && (
                        <tr><td colSpan="4" className="py-6 text-center text-gray-600 text-xs uppercase tracking-widest">No hay familiares registrados.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* PESTAÑA 2: INSTITUCIONAL */}
          {activeTab === 'institucional' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fade-in">
              <div className="bg-[#050505] border border-white/5 p-8 shadow-lg">
                <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                  <h3 className="font-serif text-2xl text-white m-0">Identidad Institucional</h3>
                  {currentUserRole === 'MAESTRO' && !editandoLogia && (
                    <button onClick={() => setEditandoLogia(true)} className="text-gray-500 hover:text-[#C5A059] transition-colors"><Edit size={16}/></button>
                  )}
                  {currentUserRole === 'MAESTRO' && editandoLogia && (
                    <button onClick={handleGuardarLogia} className="text-[#C5A059] hover:text-white transition-colors flex items-center gap-1 text-[10px] uppercase tracking-widest"><Save size={14}/> Guardar</button>
                  )}
                </div>

                <div className="flex flex-col gap-6">
                  {editandoLogia ? (
                    <>
                      <div className="flex flex-col gap-2">
                        <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Nombre de la Logia</label>
                        <input type="text" value={logiaData.nombre_logia || ''} onChange={(e) => setLogiaData({...logiaData, nombre_logia: e.target.value})} className="bg-[#111111] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Valle</label>
                        <input type="text" value={logiaData.valle || ''} onChange={(e) => setLogiaData({...logiaData, valle: e.target.value})} className="bg-[#111111] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Primer Venerable Maestro</label>
                        <input type="text" value={logiaData.primer_venerable_maestro || ''} onChange={(e) => setLogiaData({...logiaData, primer_venerable_maestro: e.target.value})} className="bg-[#111111] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
                      </div>
                    </>
                  ) : (
                    <>
                      <InfoItem label="Nombre de la Logia" value={`${logiaData.nombre_logia || 'Sin Registro'} N° ${logiaData.numero_logia || ''}`} />
                      <InfoItem label="Valle" value={`Valle de ${logiaData.valle || 'Sin Registro'}`} />
                      <InfoItem label="Primer Venerable Maestro" value={logiaData.primer_venerable_maestro || 'No registrado'} />
                    </>
                  )}
                </div>
              </div>

              <div className="bg-[#050505] border border-white/5 p-8 shadow-lg">
                <h3 className="font-serif text-2xl text-white m-0 mb-6 border-b border-white/10 pb-4">Títulos Obtenidos</h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-4">
                  <li className="flex items-start gap-3 text-sm text-gray-300 bg-white/5 p-4 border border-white/5">
                    <Award size={18} className="text-[#C5A059] mt-[2px] flex-shrink-0"/> 
                    <div><strong className="text-white block mb-1 tracking-wide">Benemérita</strong><span className="text-gray-500 font-light font-mono text-xs">Dec. GLP N° 045</span></div>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* PESTAÑA 3: DIRECTORIO */}
          {activeTab === 'directorio' && (currentUserRole === 'COMPAÑERO' || currentUserRole === 'MAESTRO') && (
            <div className="flex flex-col gap-8 animate-fade-in">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                  <h3 className="font-serif text-2xl text-white m-0 mb-2">Gestión de Miembros</h3>
                  <p className="text-gray-400 font-light text-sm m-0">Administra los perfiles de tu Logia. Haz clic en un hermano para ver/editar su información.</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  {currentUserRole === 'MAESTRO' && (
                    <button 
                      onClick={() => setIsModalMiembroOpen(true)}
                      className="flex items-center justify-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold transition-all duration-300 bg-[#C5A059] text-[#050505] hover:bg-white"
                    >
                      <Plus size={16} /> Alta Manual
                    </button>
                  )}
                  <button onClick={handleCopiarEnlace} className={`flex items-center justify-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold transition-all duration-300 ${linkCopiado ? 'bg-green-600 text-white border-none' : 'bg-transparent border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#050505]'}`}>
                    {linkCopiado ? <><CheckCircle size={16}/> ¡Enlace Copiado!</> : <><LinkIcon size={16}/> Copiar Enlace Público</>}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto bg-[#050505] border border-white/5">
                <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Hermano</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">DNI</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Grado</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Contacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {miembrosLogia.map((miembro) => (
                      <tr 
                        key={miembro.id} 
                        onClick={() => setMiembroSeleccionado(miembro)}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      >
                        <td className="py-4 px-6 text-white font-medium text-sm">{miembro.nombres} {miembro.apellidos}</td>
                        <td className="py-4 px-6 text-gray-400 text-xs font-mono">{miembro.dni}</td>
                        <td className="py-4 px-6"><span className={`px-3 py-1 text-[9px] tracking-widest uppercase font-mono border ${miembro.grado === 'MAESTRO' ? 'bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/50' : 'bg-blue-900/30 text-blue-300 border-blue-800/50'}`}>{miembro.grado}</span></td>
                        <td className="py-4 px-6">
                          <span className="block text-gray-300 text-sm">{miembro.email}</span>
                          <span className="block text-gray-500 text-xs mt-1 font-mono">{miembro.telefono || '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>

      {isModalFamiliarOpen && <NuevoFamiliarModal onClose={() => setIsModalFamiliarOpen(false)} onSave={handleAgregarFamiliar} />}
      {isModalMiembroOpen && <AltaManualModal onClose={() => setIsModalMiembroOpen(false)} onSave={handleAltaManual} />}
      {miembroSeleccionado && <DetalleMiembroModal miembro={miembroSeleccionado} onClose={() => setMiembroSeleccionado(null)} />}
    </div>
  );
}

// --- SUBCOMPONENTES ---

function InfoItem({ label, value }) {
  return (
    <div>
      <span className="block text-[10px] tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-1">{label}</span>
      <span className="block text-sm text-gray-300 font-light">{value}</span>
    </div>
  );
}

function NuevoFamiliarModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({ nombres: '', parentesco: '', edad: '', profesion: '' });

  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="fixed inset-0 bg-[#050505]/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-lg p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">Añadir Familiar</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white cursor-pointer p-1"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Nombre Completo</label>
            <input type="text" name="nombres" required onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#C5A059] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Parentesco</label>
              <input type="text" name="parentesco" required placeholder="Ej. Esposa, Hijo" onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#C5A059] outline-none" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Edad</label>
              <input type="number" name="edad" required onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#C5A059] outline-none" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Profesión / Ocupación</label>
            <input type="text" name="profesion" onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#C5A059] outline-none" />
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Añadir</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AltaManualModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({ 
    dni: '', nombres: '', apellidos: '', email: '', telefono: '', grado: 'APRENDIZ', estado: 'activo', password: '' 
  });

  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="fixed inset-0 bg-[#050505]/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-lg p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">Alta Manual de Hermano</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white cursor-pointer p-1"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Nombres</label>
              <input type="text" name="nombres" required onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Apellidos</label>
              <input type="text" name="apellidos" required onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
             <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">DNI</label>
              <input type="text" name="dni" required onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Teléfono</label>
              <input type="text" name="telefono" onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Correo Electrónico</label>
            <input type="email" name="email" required onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Grado</label>
              <select name="grado" onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none appearance-none cursor-pointer">
                <option value="APRENDIZ">Aprendiz</option>
                <option value="COMPAÑERO">Compañero</option>
                <option value="MAESTRO">Maestro</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Estado</label>
              <select name="estado" onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none appearance-none cursor-pointer">
                <option value="activo">Activo</option>
                <option value="pendiente">Pendiente</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Contraseña (Opcional - DNI por defecto)</label>
            <input type="password" name="password" placeholder="Dejar vacío para usar el DNI" onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Registrar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetalleMiembroModal({ miembro, onClose }) {
  const [reconocimientos, setReconocimientos] = useState([]);
  const [isAddingRec, setIsAddingRec] = useState(false);
  const [newRec, setNewRec] = useState({ titulo: '', descripcion: '', fecha_otorgamiento: '' });

  useEffect(() => {
    cargarReconocimientos();
  }, [miembro.id]);

  const cargarReconocimientos = async () => {
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/${miembro.id}/reconocimientos`);
      const data = await res.json();
      if (data.success) setReconocimientos(data.data);
    } catch (error) { console.error("Error", error); }
  };

  const handleAgregarReconocimiento = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/usuarios/${miembro.id}/reconocimientos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRec)
      });
      const data = await res.json();
      if (data.success) {
        setIsAddingRec(false);
        setNewRec({ titulo: '', descripcion: '', fecha_otorgamiento: '' });
        cargarReconocimientos();
      }
    } catch (error) { alert('Error al añadir reconocimiento.'); }
  };

  const canAddReconocimiento = miembro.grado === 'APRENDIZ' || miembro.grado === 'COMPAÑERO';

  return (
    <div className="fixed inset-0 bg-[#050505]/90 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-2xl max-h-[90vh] overflow-y-auto hide-scrollbar p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#111111] to-[#050505] border border-[#C5A059]/40 text-[#C5A059] flex justify-center items-center font-serif text-xl">
              {miembro.nombres.charAt(0)}
            </div>
            <div>
              <h2 className="font-serif text-2xl text-white m-0">{miembro.nombres} {miembro.apellidos}</h2>
              <span className="text-gray-400 font-mono text-[10px] tracking-widest uppercase">{miembro.grado}</span>
            </div>
          </div>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white cursor-pointer p-1"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <InfoItem label="DNI" value={miembro.dni} />
          <InfoItem label="Correo" value={miembro.email} />
          <InfoItem label="Teléfono" value={miembro.telefono || 'No registrado'} />
          <InfoItem label="Estado" value={<span className="text-green-400 uppercase text-xs tracking-widest">{miembro.estado}</span>} />
        </div>

        <div className="flex justify-between items-end mb-4 border-b border-white/10 pb-3">
          <h3 className="font-serif text-xl text-white m-0 flex items-center gap-2"><Shield size={18} className="text-[#C5A059]"/> Histórico y Cargos</h3>
          {canAddReconocimiento && !isAddingRec && (
            <button onClick={() => setIsAddingRec(true)} className="text-[#C5A059] text-[10px] tracking-widest uppercase hover:text-white transition-colors flex items-center gap-1">
              <Plus size={14} /> Añadir
            </button>
          )}
        </div>

        {isAddingRec && (
          <form onSubmit={handleAgregarReconocimiento} className="bg-[#111111] border border-white/10 p-4 mb-6 flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Cargo / Reconocimiento</label>
                <input type="text" required placeholder="Ej. Primer Vigilante" value={newRec.titulo} onChange={(e) => setNewRec({...newRec, titulo: e.target.value})} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha de Otorgamiento</label>
                <input type="date" required value={newRec.fecha_otorgamiento} onChange={(e) => setNewRec({...newRec, fecha_otorgamiento: e.target.value})} className="bg-[#050505] border border-white/10 text-gray-400 px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Descripción (Opcional)</label>
              <input type="text" value={newRec.descripcion} onChange={(e) => setNewRec({...newRec, descripcion: e.target.value})} className="bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#C5A059] outline-none" />
            </div>
            <div className="flex justify-end gap-3 mt-2">
              <button type="button" onClick={() => setIsAddingRec(false)} className="bg-transparent border border-white/20 text-gray-300 px-4 py-2 text-[10px] tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
              <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-4 py-2 text-[10px] tracking-widest uppercase font-bold transition-colors">Guardar</button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto bg-[#050505] border border-white/5">
          <table className="w-full text-left border-collapse min-w-[500px]">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Título</th>
                <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Descripción</th>
                <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {reconocimientos.map((rec) => (
                <tr key={rec.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-sm text-gray-300 font-medium">{rec.titulo}</td>
                  <td className="py-3 px-4 text-sm text-gray-400">{rec.descripcion || '-'}</td>
                  <td className="py-3 px-4 text-sm text-gray-400 font-mono">{rec.fecha_otorgamiento ? new Date(rec.fecha_otorgamiento).toLocaleDateString('es-PE') : '-'}</td>
                </tr>
              ))}
              {reconocimientos.length === 0 && (
                <tr><td colSpan="3" className="py-6 text-center text-gray-600 text-[10px] uppercase tracking-widest">Sin reconocimientos registrados.</td></tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}